import { Link } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Navigation */}
      <nav className="bg-[#0B0F19] border-b border-[#D4AF37]/10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center shadow-lg">
              <span className="text-[#020617] font-bold text-lg tracking-tight">A+</span>
            </div>
            <span className="font-['Playfair_Display'] text-2xl text-white">Advanta <span className="text-[#D4AF37]">Plus</span></span>
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F5DEB3] mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="bg-[#0B0F19] rounded-2xl p-8 border border-[#D4AF37]/10">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-[#D4AF37]" />
            <h1 className="font-['Playfair_Display'] text-3xl text-white">Privacy Policy</h1>
          </div>

          <div className="prose prose-invert max-w-none space-y-6 text-white/80">
            <p className="text-white/60 text-sm">Last updated: March 2026</p>

            <section>
              <h2 className="text-xl text-white font-semibold mt-8 mb-4">1. Introduction</h2>
              <p>
                Advanta Plus ("we", "our", or "us") is committed to protecting your personal data and respecting your privacy. 
                This Privacy Policy explains how we collect, use, store, and protect your information when you use our travel 
                membership services.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-semibold mt-8 mb-4">2. Data We Collect</h2>
              <p>We collect the following types of personal data:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Account Information:</strong> Name, email address, password (encrypted)</li>
                <li><strong>Membership Data:</strong> Membership tier, purchase history, card usage</li>
                <li><strong>Payment Information:</strong> Processed securely through Stripe - we do not store card details</li>
                <li><strong>Usage Data:</strong> How you interact with our services, businesses visited</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-semibold mt-8 mb-4">3. How We Use Your Data</h2>
              <p>We use your personal data to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide and manage your membership</li>
                <li>Process payments and send receipts</li>
                <li>Verify membership status at partner businesses</li>
                <li>Send important account notifications</li>
                <li>Improve our services and user experience</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-semibold mt-8 mb-4">4. Legal Basis for Processing (GDPR)</h2>
              <p>Under the General Data Protection Regulation (GDPR), we process your data based on:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Contract:</strong> To fulfill our membership agreement with you</li>
                <li><strong>Consent:</strong> For marketing communications (you can opt-out anytime)</li>
                <li><strong>Legitimate Interest:</strong> To improve and secure our services</li>
                <li><strong>Legal Obligation:</strong> To comply with tax and business regulations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-semibold mt-8 mb-4">5. Data Sharing</h2>
              <p>We share your data only with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Partner Businesses:</strong> Basic membership verification (name, validity status)</li>
                <li><strong>Payment Processors:</strong> Stripe for secure payment processing</li>
                <li><strong>Affiliates:</strong> Commission tracking (anonymized data)</li>
              </ul>
              <p className="mt-4">We never sell your personal data to third parties.</p>
            </section>

            <section>
              <h2 className="text-xl text-white font-semibold mt-8 mb-4">6. Your Rights</h2>
              <p>Under GDPR, you have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Correct inaccurate data</li>
                <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                <li><strong>Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Objection:</strong> Object to certain processing activities</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
              </ul>
              <p className="mt-4">To exercise these rights, contact us at privacy@advanta.plus</p>
            </section>

            <section>
              <h2 className="text-xl text-white font-semibold mt-8 mb-4">7. Data Retention</h2>
              <p>
                We retain your personal data for as long as your account is active or as needed to provide services. 
                After account deletion, we may retain certain data for up to 7 years for legal and tax compliance purposes.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-semibold mt-8 mb-4">8. Data Security</h2>
              <p>
                We implement industry-standard security measures including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Encryption of data in transit (SSL/TLS) and at rest</li>
                <li>Secure password hashing (bcrypt)</li>
                <li>Regular security audits</li>
                <li>Limited access controls</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-semibold mt-8 mb-4">9. Cookies</h2>
              <p>
                We use essential cookies for authentication and session management. 
                No tracking cookies are used without your consent.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-semibold mt-8 mb-4">10. Contact Us</h2>
              <p>
                For any privacy-related questions or to exercise your rights:
              </p>
              <p className="mt-2">
                <strong>Email:</strong> privacy@advanta.plus<br/>
                <strong>Address:</strong> Costa del Sol, Spain
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
